import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { Supplier } from '../../services/suppliersApi';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoreHorizontal, Search, Trash2, FileText } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeleteSupplier } from '../../hooks/useSuppliers';
import { EditSupplierDialog } from './EditSuppliersDialog';
import { useConfirm, withConfirmSuccess } from '@/shared/contexts/confirm-context';

interface SuppliersTableProps {
  suppliers: Supplier[];
}

export const SuppliersTable = ({ suppliers }: SuppliersTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const deleteMutation = useDeleteSupplier();
  const confirm = useConfirm();

  const filteredSuppliers = suppliers.filter(
    (supplier) =>
      supplier.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.supplierCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.phoneNumber?.includes(searchTerm)
  );

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: 'Delete supplier?',
      description: `“${name}” will be permanently removed. This can't be undone.`,
      variant: 'destructive',
      confirmLabel: 'Delete',
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(id), `${name} deleted`),
    });
  };

  const handleGenerateApInvoice = (supplier: Supplier) => {
    navigate(`/invoicing?tab=ap&openGenerate=1&supplierId=${supplier.id}`);
  };

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Supplier Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Lead Time</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSuppliers.map((supplier) => (
              <TableRow key={supplier.id}>
                <TableCell className="font-medium">
                  {supplier.supplierName}
                </TableCell>
                <TableCell className="text-sm">
                  {supplier.supplierCode}
                </TableCell>
                <TableCell className="text-sm">
                  {supplier.contactPerson}
                </TableCell>
                <TableCell className="text-sm">{supplier.email}</TableCell>
                <TableCell className="text-sm">
                  {supplier.phoneNumber}
                </TableCell>
                <TableCell className="text-sm">
                  {supplier.town}, {supplier.county}
                </TableCell>
                <TableCell className="text-sm">
                  {supplier.leadTimeDays} days
                </TableCell>
                <TableCell>
                  <Badge
                    variant={supplier.isActive ? 'default' : 'destructive'}
                  >
                    {supplier.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <EditSupplierDialog supplier={supplier} />
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleGenerateApInvoice(supplier)}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Generate AP Invoice
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() =>
                          handleDelete(supplier.id, supplier.supplierName)
                        }
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {filteredSuppliers.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No suppliers found
        </div>
      )}
    </div>
  );
};